import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SITE_URL } from "@/lib/utils";

export const Route = createFileRoute("/auth/confirm")({
  head: () => ({
    meta: [
      { title: "Confirm email — Pluggz" },
      { name: "description", content: "Confirm your Pluggz email address." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/auth/confirm` }],
  }),
  component: ConfirmPage,
});

function ConfirmPage() {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");

  useEffect(() => {
    async function run() {
      try {
        const hash = window.location.hash.startsWith("#")
          ? window.location.hash.slice(1)
          : window.location.hash;
        const params = new URLSearchParams(hash);
        const access_token = params.get("access_token");
        const refresh_token = params.get("refresh_token");
        const type = params.get("type");
        const errorDescription = params.get("error_description");

        if (errorDescription || !access_token || !refresh_token) {
          setStatus("error");
          return;
        }

        if (type && type !== "signup" && type !== "email_change") {
          setStatus("error");
          return;
        }

        const { error } = await supabase.auth.setSession({ access_token, refresh_token });
        if (error) {
          setStatus("error");
          return;
        }
        setStatus("success");
        // Strip hash from URL
        window.history.replaceState(null, "", window.location.pathname);
      } catch {
        setStatus("error");
      }
    }
    run();
  }, []);

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden px-4 py-12 sm:px-6 sm:py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-48 w-48 rounded-full bg-accent/20 blur-3xl float-slow sm:h-64 sm:w-64" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-sand/30 blur-3xl float-slow sm:h-64 sm:w-64" style={{ animationDelay: "-3s" }} />
      <div className="relative">
        {status === "loading" && (
          <>
            <p className="editorial-eyebrow">One moment</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
              Confirming<span className="italic shimmer">…</span>
            </h1>
            <p className="mt-6 text-sm text-muted-foreground">Verifying your email link.</p>
          </>
        )}

        {status === "success" && (
          <>
            <p className="editorial-eyebrow">You're in</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
              Email <span className="italic shimmer">confirmed</span>
            </h1>
            <p className="mt-6 text-sm text-muted-foreground">
              Email confirmed — welcome to Pluggz!
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex w-full items-center justify-center bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01]"
            >
              Enter Pluggz
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <p className="editorial-eyebrow">Link expired</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
              Try <span className="italic shimmer">again</span>
            </h1>
            <p className="mt-6 text-sm text-muted-foreground">
              This confirmation link has expired.
            </p>
            <Link
              to="/signup"
              className="mt-8 inline-flex w-full items-center justify-center bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01]"
            >
              Back to sign up
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
